import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { useTheme } from '../contexts/ThemeContext';
import { onGeostack, PORTAL_URL } from '../lib/portal';
import { useDemoMode } from '../hooks/useDemoMode';

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function CompanyProfileModal({ onClose }) {
  const { C } = useTheme();
  const { user } = useAuth();
  const { selectedOrg } = useOrg();

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const createdDate = formatDate(selectedOrg?.created_at);
  const isOwner = !!(selectedOrg?.owner_id && user?.id && selectedOrg.owner_id === user.id);

  const row = (label, value, mono) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 12, color: C.text2 }}>{label}</span>
      <span style={{ fontSize: 12, color: C.text0, fontFamily: mono ? 'monospace' : undefined, textAlign: 'right', wordBreak: 'break-all' }}>
        {value}
      </span>
    </div>
  );

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
    >
      <div style={{ width: 380, maxWidth: '90vw', background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.35)', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.text0 }}>{selectedOrg?.name || 'Company Profile'}</div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', color: C.text2, fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: 4 }}
          >
            ×
          </button>
        </div>
        {!selectedOrg ? (
          <div style={{ fontSize: 12, color: C.text3, fontStyle: 'italic', padding: '12px 0' }}>No organization selected.</div>
        ) : (
          <div>
            {row('Description', selectedOrg?.description || '—')}
            {row('Created', createdDate || '—')}
            {row('Your role', isOwner ? 'Owner' : 'Member')}
            {row('Organization ID', selectedOrg?.id || '—', true)}
          </div>
        )}
      </div>
    </div>
  );
}

function AccountMenu() {
  const { user, profile, signOut } = useAuth();
  const { organizations, selectedOrgId, setSelectedOrgId } = useOrg();
  const { C } = useTheme();
  const navigate = useNavigate();
  const [demoMode, setDemoMode] = useDemoMode();

  const [open, setOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const menuRef = useRef(null);

  const displayName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ')
    || user?.email;
  const avatarChar = (profile?.first_name || user?.email || 'U').charAt(0).toUpperCase();

  useEffect(() => {
    if (!open) return undefined;

    const onMouseDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const closeMenu = () => setOpen(false);

  const itemStyle = {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
    padding: '9px 12px', background: 'transparent', border: 'none', cursor: 'pointer',
    fontSize: 12, color: C.text1, borderRadius: 6,
  };

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px 2px 8px', borderRadius: 8 }}
      >
        <span style={{ fontSize: 12, color: C.text1 }}>{displayName}</span>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: C.accentBg, border: `1px solid ${C.accentBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: C.cyan, flexShrink: 0 }}>
          {avatarChar}
        </div>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 260,
            background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0,0,0,0.35)', zIndex: 40, padding: 8,
          }}
        >
          {/* Identity header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 8px 12px' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: C.accentBg, border: `1px solid ${C.accentBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: C.cyan, flexShrink: 0 }}>
              {avatarChar}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {displayName}
              </div>
              <div style={{ fontSize: 11, color: C.text2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.email}
              </div>
            </div>
          </div>

          <div style={{ height: 1, background: C.border, margin: '4px 0 8px' }} />

          {/* Companies */}
          <div style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.text3, padding: '0 12px 6px' }}>
            Companies
          </div>
          {organizations.length === 0 ? (
            <div style={{ fontSize: 12, color: C.text3, fontStyle: 'italic', padding: '6px 12px 10px' }}>
              No organization
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 8 }}>
              {organizations.map(org => {
                const active = org.id === selectedOrgId;
                return (
                  <button
                    key={org.id}
                    onClick={() => { setSelectedOrgId(org.id); closeMenu(); }}
                    style={{ ...itemStyle, background: active ? C.bg3 : 'transparent', justifyContent: 'space-between' }}
                    onMouseEnter={e => { if (!active) e.currentTarget.style.background = C.bg3; }}
                    onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <span>🏢</span>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{org.name}</span>
                    </span>
                    {active && <span style={{ color: C.cyan, flexShrink: 0 }}>✓</span>}
                  </button>
                );
              })}
            </div>
          )}

          {/* Demo: the Cape Cod sample data is opt-in and shares one preference with the map ribbon. */}
          <button
            role="switch"
            aria-checked={demoMode}
            onClick={() => setDemoMode(!demoMode)}
            style={{ ...itemStyle, justifyContent: 'space-between', marginBottom: 8 }}
            onMouseEnter={e => { e.currentTarget.style.background = C.bg3; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>🧪</span>
              <span>Demo data (Cape Cod sample)</span>
            </span>
            <span aria-hidden="true" style={{ width: 28, height: 16, borderRadius: 8, background: demoMode ? C.cyan : C.border, position: 'relative', flexShrink: 0 }}>
              <span style={{ position: 'absolute', top: 2, left: demoMode ? 14 : 2, width: 12, height: 12, borderRadius: '50%', background: '#fff' }} />
            </span>
          </button>

          <div style={{ height: 1, background: C.border, margin: '4px 0 8px' }} />

          <button
            onClick={() => { setShowProfileModal(true); closeMenu(); }}
            style={itemStyle}
            onMouseEnter={e => { e.currentTarget.style.background = C.bg3; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <span>🏛️</span>
            <span>Company Profile</span>
          </button>

          <button
            onClick={() => { navigate('/settings'); closeMenu(); }}
            style={itemStyle}
            onMouseEnter={e => { e.currentTarget.style.background = C.bg3; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <span>⚙️</span>
            <span>Settings</span>
          </button>

          <div style={{ height: 1, background: C.border, margin: '4px 0 4px' }} />

          {/* On geostack.com the session belongs to the portal and every app shares it, so this
              only leaves the app; signing out is the portal's to do. */}
          {onGeostack() ? (
            <button
              onClick={() => { closeMenu(); window.location.assign(PORTAL_URL); }}
              style={itemStyle}
              onMouseEnter={e => { e.currentTarget.style.background = C.bg3; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span>🚪</span>
              <span>Exit to Portal</span>
            </button>
          ) : (
            <button
              onClick={() => { closeMenu(); signOut(); }}
              style={{ ...itemStyle, color: '#f87171' }}
              onMouseEnter={e => { e.currentTarget.style.background = C.bg3; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span>🚪</span>
              <span>Sign Out</span>
            </button>
          )}
        </div>
      )}

      {showProfileModal && (
        <CompanyProfileModal onClose={() => setShowProfileModal(false)} />
      )}
    </div>
  );
}

export default AccountMenu;
